// Derives the user-facing reason string for an auto-RAG change that occurred on
// a deliverable lifecycle transition. Covers rejection (red), acceptance (green),
// the submitted family (green on-time / amber after due date), and conditional
// acceptance (amber). Returns null when no banner should fire — manual override,
// no rag change, or insufficient date data on a submission.
//
// Extracted from DeliverableTab as a leaf module so it can be unit-tested
// directly (the component is heavily imported/mocked).
//
// prevRagRating: the rating stored on the item immediately before the mutation
// was fired (captured from caller scope, not from the detail cache).
//
// IMPORTANT: a late submission's PUT response carries the DERIVED status "late"
// (not the stored "submitted"), so the submitted-family branch keys off
// submittedAt + dueDate rather than an exact status === "submitted" check —
// otherwise the amber "after due date" banner is unreachable for late
// submissions (the bug the e2e late-submission spec caught).
import type { Deliverable, RagRating } from "../../../../types";

export function deriveRagBannerReason(
  prevRagRating: RagRating | undefined,
  next: Deliverable,
): string | null {
  if (next.ragSource !== "derived") return null;
  if (next.ragRating === prevRagRating) return null;

  if (next.status === "rejected") {
    return "RAG set to Red after rejection";
  }

  if (next.status === "accepted") {
    return next.ragRating === "green" ? "RAG set to Green after acceptance" : null;
  }

  // Conditional acceptance auto-derives amber (the cycle isn't terminal —
  // conditions still need to clear). The PUT response carries the derived
  // status "conditionally-accepted"; the ragRating-changed guard above
  // already suppresses the no-churn case where the row was already amber.
  if (next.status === "conditionally-accepted") {
    return next.ragRating === "amber"
      ? "RAG set to Amber after conditional acceptance"
      : null;
  }

  // Submitted family: stored "submitted" OR the derived "late" (past-due
  // submission). The cast compares against a value outside the stored
  // DeliverableStatus enum without tripping the no-overlap check.
  const isSubmittedFamily =
    next.status === "submitted" || (next.status as string) === "late";
  if (isSubmittedFamily) {
    if (next.submittedAt == null || next.dueDate == null) return null;
    const submittedDay = next.submittedAt.slice(0, 10);
    const dueDay = next.dueDate.slice(0, 10);
    if (submittedDay <= dueDay) {
      return "RAG set to Green after on-time submission";
    }
    return "RAG set to Amber — submitted after due date";
  }

  return null;
}
